import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Collapse, Form } from "react-bootstrap";
import { HelpLink, useHelpLocation, useHelpNavigate } from "./help/HelpNavigation.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faChevronDown,
  faChevronRight,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { Platform02GuidePage, getGuideIndexItems } from "./Platform02GuideContent.js";
import {
  GUIDE_NAVIGATION,
  normalizeGuideSearch,
  searchGuidePages,
  guideHeadingId,
} from "./Platform02GuideData.js";

const linkProps = (linkState, onNavigate) => ({
  state: linkState,
  onClick: onNavigate,
});

const GuideNavigationTree = ({
  activePageId,
  expandedIds,
  navigation,
  linkState,
  onNavigate,
  onToggle,
}) => {
  const renderNodes = (nodes, depth = 0) => nodes.map((node) => {
    const hasChildren = (node.children || []).length > 0;
    const isExpanded = expandedIds.includes(node.id);
    const isActive = node.id === activePageId;

    return (
      <div className="platform02-guide-nav-node" key={node.id}>
        <div
          className={`platform02-guide-nav-row ${isActive ? "active" : ""} ${depth === 0 ? "root" : ""}`}
          style={{ "--platform02-guide-nav-depth": depth }}
        >
          <HelpLink
            to={node.href}
            className="platform02-guide-nav-page"
            aria-current={isActive ? "page" : undefined}
            {...linkProps(linkState, () => onNavigate(node))}
          >
            {node.title}
          </HelpLink>
          {hasChildren && (
            <button
              type="button"
              className="platform02-guide-nav-toggle"
              onClick={() => onToggle(node.id)}
              aria-label={`${isExpanded ? "Recolher" : "Expandir"} ${node.title}`}
              aria-expanded={isExpanded}
            >
              <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
            </button>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div className="platform02-guide-nav-children">
            {renderNodes(node.children, depth + 1)}
          </div>
        )}
      </div>
    );
  });

  return <nav className="platform02-guide-nav-tree" aria-label="Páginas do guia">{renderNodes(navigation)}</nav>;
};

const getHighlightRanges = (text, query) => {
  const originalCharacters = Array.from(text || "");
  let normalizedText = "";
  const originalIndexByNormalizedIndex = [];

  originalCharacters.forEach((character, originalIndex) => {
    const normalizedCharacter = normalizeGuideSearch(character);
    Array.from(normalizedCharacter).forEach((normalizedPart) => {
      normalizedText += normalizedPart;
      originalIndexByNormalizedIndex.push(originalIndex);
    });
    if (!normalizedCharacter && /\s/.test(character)) {
      normalizedText += " ";
      originalIndexByNormalizedIndex.push(originalIndex);
    }
  });

  const tokens = normalizeGuideSearch(query).split(/\s+/).filter(Boolean);
  const ranges = [];
  tokens.forEach((token) => {
    let matchIndex = normalizedText.indexOf(token);
    while (matchIndex >= 0) {
      const start = originalIndexByNormalizedIndex[matchIndex];
      const end = originalIndexByNormalizedIndex[matchIndex + token.length - 1];
      if (start !== undefined && end !== undefined) ranges.push([start, end + 1]);
      matchIndex = normalizedText.indexOf(token, matchIndex + token.length);
    }
  });

  return ranges
    .sort((first, second) => first[0] - second[0])
    .reduce((merged, range) => {
      const previous = merged[merged.length - 1];
      if (previous && range[0] <= previous[1]) previous[1] = Math.max(previous[1], range[1]);
      else merged.push([...range]);
      return merged;
    }, []);
};

const HighlightMatch = ({ text, query }) => {
  const ranges = getHighlightRanges(text, query);
  if (ranges.length === 0) return text;
  const parts = [];
  let cursor = 0;
  ranges.forEach(([start, end], index) => {
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(<mark key={`${start}-${end}-${index}`}>{text.slice(start, end)}</mark>);
    cursor = end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
};

const GuideNavigationPanel = ({
  activePageId,
  expandedIds,
  instanceId,
  linkState,
  onNavigate,
  onToggle,
  searchQuery,
  setSearchQuery,
}) => {
  const navigate = useHelpNavigate();
  const results = useMemo(() => searchGuidePages(searchQuery), [searchQuery]);
  const [activeResultIndex, setActiveResultIndex] = useState(0);
  const resultRefs = useRef([]);
  const isSearching = normalizeGuideSearch(searchQuery).length > 0;
  const resultsId = `platform02-guide-search-results-${instanceId}`;

  useEffect(() => {
    setActiveResultIndex(0);
    resultRefs.current = [];
  }, [searchQuery]);

  const handleKeyDown = (event) => {
    if (!isSearching) return;
    if (event.key === "ArrowDown" && results.length > 0) {
      event.preventDefault();
      setActiveResultIndex((current) => (current + 1) % results.length);
    } else if (event.key === "ArrowUp" && results.length > 0) {
      event.preventDefault();
      setActiveResultIndex((current) => (current - 1 + results.length) % results.length);
    } else if (event.key === "Enter" && results[activeResultIndex]) {
      event.preventDefault();
      const result = results[activeResultIndex];
      onNavigate();
      navigate(result.href, { state: linkState });
    } else if (event.key === "Escape") {
      event.preventDefault();
      setSearchQuery("");
    }
  };

  return (
    <>
      <div className="platform02-guide-search">
        <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
        <Form.Control
          type="search"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Buscar páginas e tópicos"
          aria-label="Buscar no guia"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isSearching}
          aria-controls={resultsId}
          aria-activedescendant={isSearching && results[activeResultIndex] ? `guide-result-${instanceId}-${activeResultIndex}` : undefined}
        />
      </div>
      {isSearching ? (
        <div id={resultsId} className="platform02-guide-search-results" role="listbox">
          <div className="platform02-guide-search-count" aria-live="polite">
            {results.length} {results.length === 1 ? "resultado" : "resultados"}
          </div>
          {results.map((result, index) => (
            <HelpLink
              id={`guide-result-${instanceId}-${index}`}
              role="option"
              aria-selected={index === activeResultIndex}
              className={`platform02-guide-search-result ${index === activeResultIndex ? "active" : ""}`}
              key={result.id}
              to={result.href}
              ref={(element) => { resultRefs.current[index] = element; }}
              onMouseEnter={() => setActiveResultIndex(index)}
              {...linkProps(linkState, onNavigate)}
            >
              <span className="platform02-guide-search-result-type">
                {result.type === "section" ? "Tópico" : "Página"}
              </span>
              <strong><HighlightMatch text={result.title} query={searchQuery} /></strong>
              {result.type === "section" && <span>{result.pageTitle}</span>}
              <small>{result.breadcrumb}</small>
              <p><HighlightMatch text={result.excerpt} query={searchQuery} /></p>
            </HelpLink>
          ))}
          {results.length === 0 && (
            <div className="platform02-guide-empty-search">
              <strong>Nenhuma página encontrada</strong>
              <span>Tente outro título ou tópico.</span>
            </div>
          )}
        </div>
      ) : (
        <GuideNavigationTree
          activePageId={activePageId}
          expandedIds={expandedIds}
          navigation={GUIDE_NAVIGATION}
          linkState={linkState}
          onNavigate={onNavigate}
          onToggle={onToggle}
        />
      )}
    </>
  );
};

export const Platform02GuideExplorer = ({ page, linkState }) => {
  const location = useHelpLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);
  const contentRef = useRef(null);

  useEffect(() => {
    const parentIds = [
      ...page.path
      .filter((item) => item.id !== page.id)
      .map((item) => item.id),
      ...(page.children?.length ? [page.id] : []),
    ];
    setExpandedIds((current) => [...new Set([...current, ...parentIds])]);
  }, [page]);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      if (location.hash) {
        const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
        if (target) {
          target.scrollIntoView({ block: "start" });
          return;
        }
      }
      contentRef.current?.scrollTo?.({ top: 0 });
    });
  }, [location.hash, page.id]);

  const toggleNavigationNode = (nodeId) => {
    setExpandedIds((current) => current.includes(nodeId)
      ? current.filter((id) => id !== nodeId)
      : [...current, nodeId]);
  };

  const handleNavigate = (node) => {
    setMobileNavigationOpen(false);
    setSearchQuery("");
    if (node?.children?.length) {
      setExpandedIds((current) => [...new Set([...current, node.id])]);
    }
  };

  const indexSections = getGuideIndexItems(page);
  const indexItems = indexSections.length > 0
    ? indexSections.map((title) => ({
      title,
      href: `${page.href}#${guideHeadingId(page.id, title)}`,
      type: "anchor",
    }))
    : (page.children || []).map((item) => ({ title: item.title, href: item.href, type: "page" }));

  const renderNavigationPanel = (instanceId) => (
    <GuideNavigationPanel
      activePageId={page.id}
      expandedIds={expandedIds}
      instanceId={instanceId}
      linkState={linkState}
      onNavigate={handleNavigate}
      onToggle={toggleNavigationNode}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
    />
  );

  return (
    <div className="platform02-guide-layout">
      <aside className="platform02-guide-sidebar">{renderNavigationPanel("desktop")}</aside>

      <main className="platform02-guide-content" ref={contentRef}>
        <div className="platform02-guide-mobile-toolbar">
          <Button
            type="button"
            variant="outline-dark"
            onClick={() => setMobileNavigationOpen((current) => !current)}
            aria-controls="platform02-guide-mobile-navigation"
            aria-expanded={mobileNavigationOpen}
          >
            <FontAwesomeIcon icon={faBars} />
            Menu
          </Button>
          <span>{page.title}</span>
        </div>
        <Collapse in={mobileNavigationOpen}>
          <div id="platform02-guide-mobile-navigation" className="platform02-guide-mobile-navigation">
            {renderNavigationPanel("mobile")}
          </div>
        </Collapse>
        <div className="platform02-guide-content-inner">
          <Platform02GuidePage page={page} linkState={linkState} onNavigate={handleNavigate} />
        </div>
      </main>

      <aside className="platform02-guide-index">
        <div className="platform02-guide-index-label">
          {page.kind === "legacy" ? "Registro histórico" : indexSections.length > 0 ? "Nesta página" : "Nesta categoria"}
        </div>
        {indexItems.length > 0 ? (
          <nav aria-label="Índice da página">
            {indexItems.map((item) => (
              <HelpLink key={`${item.href}-${item.title}`} to={item.href} {...linkProps(linkState, handleNavigate)}>
                {item.title}
              </HelpLink>
            ))}
          </nav>
        ) : (
          <p>{page.kind === "legacy" ? "Conteúdo preservado em uma única página." : "Escolha uma categoria ou uma trilha para continuar."}</p>
        )}
      </aside>
    </div>
  );
};
